import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Alert, Chip,
  MenuItem, Divider, Tabs, Tab, Table, TableHead, TableRow, TableCell,
  TableBody, IconButton, Tooltip, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Switch, FormControlLabel, Grid
} from '@mui/material';
import {
  ArrowBack, Save, Add, Edit, Delete, Star, Person,
  Work, AccountBalance, FamilyRestroom, AttachMoney, CalculateOutlined, Receipt
} from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import AttachmentsPanel from './AttachmentsPanel';
import { fmtN, sym, locale } from '../utils/currency';
import { useDepartments }  from '../hooks/useDepartments';
import { usePayGrades }    from '../hooks/usePayGrades';
import { usePayGroups }    from '../hooks/usePayGroups';
import { useLeaveGroups }  from '../hooks/useLeaveGroups';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const STATUSES     = ['Active', 'Probation', 'On Leave', 'Terminated'];
const GENDERS      = ['Male', 'Female', 'Other'];
const RELATIONSHIPS = ['Spouse', 'Child', 'Father', 'Mother', 'Brother', 'Sister', 'Guardian', 'Other'];

const STATUS_COLOR = { Active: 'success', Probation: 'info', 'On Leave': 'warning', Terminated: 'error' };

const toDate = (v) => { try { return v ? new Date(v).toISOString().split('T')[0] : ''; } catch { return ''; } };
const age    = (dob) => { if (!dob) return null; const d = new Date(dob); const now = new Date(); return Math.floor((now - d) / (365.25 * 24 * 3600 * 1000)); };

const emptyDep = { fullName: '', relationship: '', dob: '', gender: '', phone: '', email: '', nationalId: '', address: '', occupation: '', isNextOfKin: false };

const SectionHeader = ({ label }) => (
  <Box mb={0.5} mt={1}>
    <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.8}>
      {label}
    </Typography>
    <Divider sx={{ mt: 0.5 }} />
  </Box>
);

const F = ({ label, name, value, onChange, type = 'text', select, children, sm = 6, helperText, disabled, multiline, rows }) => (
  <Grid item xs={12} sm={sm}>
    <TextField
      label={label} name={name} value={value ?? ''} onChange={onChange}
      type={type} select={!!select} fullWidth size="small"
      InputLabelProps={{ shrink: true }}
      helperText={helperText} disabled={disabled}
      multiline={multiline} rows={rows}
      SelectProps={select ? {
        displayEmpty: true,
        MenuProps: { PaperProps: { sx: { maxHeight: 280 } } }
      } : undefined}
    >
      {select && children}
    </TextField>
  </Grid>
);

const TabPanel = ({ value, index, children }) =>
  value === index ? <Box pt={2}>{children}</Box> : null;

// ─────────────────────────────────────────────────────────────────────────────

const EmployeeCard = () => {
  const navigate = useNavigate();
  const { employeeNo } = useParams();
  const [searchParams] = useSearchParams();
  const payrollCtx = searchParams.get('context') === 'payroll';
  const fromUrl    = searchParams.get('from') || '/payroll/runs';
  const { names: deptNames }  = useDepartments();
  const { payGrades }         = usePayGrades();
  const { payGroups }         = usePayGroups();
  const { leaveGroups }       = useLeaveGroups();
  const [steps, setSteps]     = useState([]);

  const [tab,        setTab]        = useState(payrollCtx ? 4 : 0);
  const [emp,        setEmp]        = useState(null);
  const [form,       setForm]       = useState({});
  const [dependants, setDependants] = useState([]);
  const [alBalance,  setAlBalance]  = useState(null);
  const [payLines,   setPayLines]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');

  // Payroll line dialog
  const emptyLine = { transactionCode: '', description: '', amount: 0, balance: 0, recurringIndex: 999, isActive: true };
  const [lineDlg,    setLineDlg]    = useState(false);
  const [lineForm,   setLineForm]   = useState(emptyLine);
  const [editLine,   setEditLine]   = useState(null);
  const [lineSaving, setLineSaving] = useState(false);
  const [lineError,  setLineError]  = useState('');
  const [txCodes,    setTxCodes]    = useState([]);

  // Dependent dialog
  const [depOpen,    setDepOpen]    = useState(false);
  const [editingDep, setEditingDep] = useState(null);
  const [depForm,    setDepForm]    = useState(emptyDep);
  const depGuard  = useUnsavedGuard({ open: depOpen, value: depForm,  onClose: () => setDepOpen(false) });
  const lineGuard = useUnsavedGuard({ open: lineDlg, value: lineForm, onClose: () => setLineDlg(false) });
  const [depSaving,  setDepSaving]  = useState(false);
  const [depError,   setDepError]   = useState('');

  // Confirm-remove dialogs
  const [removeDep,  setRemoveDep]  = useState(null);   // stores dep object
  const [removeLine, setRemoveLine] = useState(null);   // stores line object

  // Payslip history (payroll context)
  const [payslips,       setPayslips]       = useState([]);
  const [payslipsLoading, setPayslipsLoading] = useState(false);

  // Payroll preview
  const [previewDlg,     setPreviewDlg]     = useState(false);
  const [previewResult,  setPreviewResult]  = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError,   setPreviewError]   = useState('');
  const [applyLoading,   setApplyLoading]   = useState(false);
  const [applyDone,      setApplyDone]      = useState(false);

  const runPreview = async () => {
    setPreviewLoading(true); setPreviewError(''); setPreviewResult(null); setApplyDone(false);
    try {
      const res = await axios.post(`${API}/api/payroll/employees/${employeeNo}/preview`, {}, { headers: hdrs() });
      setPreviewResult(res.data);
      setPreviewDlg(true);
    } catch (err) {
      setPreviewError(err.response?.data?.error || 'Preview failed');
    } finally { setPreviewLoading(false); }
  };

  const applyPreviewToPayLines = async () => {
    if (!previewResult) return;
    setApplyLoading(true);
    try {
      const res = await axios.post(`${API}/api/payroll/employees/${employeeNo}/apply-preview`, {}, { headers: hdrs() });
      setPayLines(res.data.payLines || []);
      setApplyDone(true);
      await loadEmployee();
    } catch (err) {
      setPreviewError(err.response?.data?.error || 'Failed to apply pay lines');
    } finally { setApplyLoading(false); }
  };

  const loadEmployee = useCallback(async () => {
    setLoading(true);
    const year = new Date().getFullYear();
    try {
      const [empRes, depRes, balRes, linesRes] = await Promise.all([
        axios.get(`${API}/api/employees/${employeeNo}`, { headers: hdrs() }),
        axios.get(`${API}/api/employees/${employeeNo}/dependants`, { headers: hdrs() }),
        axios.get(`${API}/api/leave/balance/${employeeNo}?year=${year}`, { headers: hdrs() }),
        axios.get(`${API}/api/payroll/employees/${employeeNo}/lines`, { headers: hdrs() }).catch(() => ({ data: [] })),
      ]);
      const alEntry = (balRes.data || []).find(b => b.leaveTypeCode === 'AL');
      setAlBalance(alEntry ? alEntry.balance : 0);
      setPayLines(linesRes.data || []);
      const e = empRes.data;
      setEmp(e);
      setForm({
        firstName:       e.firstName       || '',
        lastName:        e.lastName        || '',
        gender:          e.gender          || '',
        dob:             toDate(e.dob),
        nationalId:      e.nationalId      || '',
        jobTitle:        e.jobTitle        || '',
        department:      e.department      || 'Teaching',
        employmentDate:  toDate(e.employmentDate),
        status:          e.status          || 'Active',
        kraPin:          e.kraPin          || '',
        nssfNo:          e.nssfNo          || '',
        nhifNo:          e.nhifNo          || '',
        basicSalary:     e.basicSalary     ?? '',
        payGradeCode:    e.payGradeCode    || '',
        payStepNo:       e.payStepNo       ?? '',
        payGroupCode:    e.payGroupCode    || '',
        leaveGroupCode:  e.leaveGroupCode  || '',
        leaveStartDate:  toDate(e.leaveStartDate),
        email:           e.email           || '',
        phone:           e.phone           || '',
        postalAddress:   e.postalAddress   || '',
        physicalAddress: e.physicalAddress || '',
        bankName:        e.bankName        || '',
        bankAccount:     e.bankAccount     || '',
        bankBranch:      e.bankBranch      || '',
        terminationDate: toDate(e.terminationDate),
        paysPAYE:        e.paysPAYE !== false,
        isPWD:           !!e.isPWD,
      });
      setDependants(depRes.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load employee');
    } finally { setLoading(false); }
  }, [employeeNo]);

  useEffect(() => { loadEmployee(); }, [loadEmployee]);

  // Load transaction codes on first visit to Pay Lines tab
  useEffect(() => {
    if ((tab !== 4 && !payrollCtx) || txCodes.length > 0) return;
    axios.get(`${API}/api/payroll/transaction-codes`, { headers: hdrs() })
      .then(r => setTxCodes(r.data || []))
      .catch(() => {});
  }, [tab]); // eslint-disable-line

  // Load payslip history when in payroll context
  useEffect(() => {
    if (!payrollCtx || !employeeNo) return;
    setPayslipsLoading(true);
    axios.get(`${API}/api/payroll/employees/${employeeNo}/entries`, { headers: hdrs() })
      .then(r => setPayslips(r.data || []))
      .catch(() => setPayslips([]))
      .finally(() => setPayslipsLoading(false));
  }, [payrollCtx, employeeNo]); // eslint-disable-line

  // Load steps whenever grade changes
  useEffect(() => {
    const code = form.payGradeCode;
    if (!code) { setSteps([]); return; }
    axios.get(`${API}/api/employees/pay-grades/${code}/steps`, { headers: hdrs() })
      .then(r => setSteps(r.data || []))
      .catch(() => setSteps([]));
  }, [form.payGradeCode]); // eslint-disable-line

  const handle = (e) => {
    const { name, value } = e.target;
    // When grade changes, clear step and keep salary
    if (name === 'payGradeCode') {
      setForm(f => ({ ...f, payGradeCode: value, payStepNo: '' }));
      return;
    }
    // When step changes, auto-fill salary
    if (name === 'payStepNo' && value !== '') {
      const step = steps.find(s => s.stepNo === Number(value));
      setForm(f => ({ ...f, payStepNo: value, basicSalary: step ? step.salary : f.basicSalary }));
      return;
    }
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    if (!form.firstName.trim()) { setError('First name is required'); return; }
    if (!form.lastName.trim())  { setError('Last name is required');  return; }
    setSaving(true); setError('');
    try {
      await axios.put(`${API}/api/employees/${employeeNo}`, {
        ...form,
        basicSalary: form.basicSalary !== '' ? Number(form.basicSalary) : 0
      }, { headers: hdrs() });
      setSuccess('Saved'); setTimeout(() => setSuccess(''), 3000);
      loadEmployee();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  // ── Dependent dialog ───────────────────────────────────────────────────────
  const openAddDep = () => { setEditingDep(null); setDepForm(emptyDep); setDepError(''); setDepOpen(true); };
  const openEditDep = (dep) => {
    setEditingDep(dep);
    setDepForm({
      fullName:     dep.fullName     || '',
      relationship: dep.relationship || '',
      dob:          toDate(dep.dob),
      gender:       dep.gender       || '',
      phone:        dep.phone        || '',
      email:        dep.email        || '',
      nationalId:   dep.nationalId   || '',
      address:      dep.address      || '',
      occupation:   dep.occupation   || '',
      isNextOfKin:  dep.isNextOfKin  || false,
    });
    setDepError(''); setDepOpen(true);
  };
  const handleDep = (e) => {
    const { name, value, type, checked } = e.target;
    setDepForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };
  const handleSaveDep = async () => {
    if (!depForm.fullName.trim())  { setDepError('Full name is required'); return; }
    if (!depForm.relationship)     { setDepError('Relationship is required'); return; }
    setDepSaving(true); setDepError('');
    try {
      if (editingDep) {
        await axios.put(`${API}/api/employees/${employeeNo}/dependants/${editingDep._id}`, depForm, { headers: hdrs() });
      } else {
        await axios.post(`${API}/api/employees/${employeeNo}/dependants`, depForm, { headers: hdrs() });
      }
      setDepOpen(false);
      const res = await axios.get(`${API}/api/employees/${employeeNo}/dependants`, { headers: hdrs() });
      setDependants(res.data || []);
    } catch (err) {
      setDepError(err.response?.data?.error || 'Save failed');
    } finally { setDepSaving(false); }
  };
  const handleDeleteDep = (dep) => { setRemoveDep(dep); };
  const confirmRemoveDep = async () => {
    const dep = removeDep;
    setRemoveDep(null);
    try {
      await axios.delete(`${API}/api/employees/${employeeNo}/dependants/${dep._id}`, { headers: hdrs() });
      setDependants(d => d.filter(x => x._id !== dep._id));
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  // ── Payroll Lines ──────────────────────────────────────────────────────────
  const openAddLine = () => {
    setEditLine(null);
    setLineForm({ ...emptyLine });
    setLineError(''); setLineDlg(true);
  };
  const openEditLine = (line) => {
    setEditLine(line);
    setLineForm({
      transactionCode: line.transactionCode || line.code || '',
      description:     line.description || '',
      amount:          line.amount ?? 0,
      balance:         line.balance ?? 0,
      recurringIndex:  line.recurringIndex ?? 999,
      isActive:        line.isActive !== false,
    });
    setLineError(''); setLineDlg(true);
  };
  const handleSaveLine = async () => {
    if (!lineForm.transactionCode) { setLineError('Transaction code is required'); return; }
    setLineSaving(true); setLineError('');
    try {
      if (editLine) {
        await axios.put(`${API}/api/payroll/employees/${employeeNo}/lines/${editLine._id}`, lineForm, { headers: hdrs() });
      } else {
        await axios.post(`${API}/api/payroll/employees/${employeeNo}/lines`, lineForm, { headers: hdrs() });
      }
      setLineDlg(false);
      const res = await axios.get(`${API}/api/payroll/employees/${employeeNo}/lines`, { headers: hdrs() });
      setPayLines(res.data || []);
    } catch (err) { setLineError(err.response?.data?.error || 'Save failed'); }
    finally { setLineSaving(false); }
  };
  const handleDeleteLine = (line) => { setRemoveLine(line); };
  const confirmRemoveLine = async () => {
    const line = removeLine;
    setRemoveLine(null);
    try {
      await axios.delete(`${API}/api/payroll/employees/${employeeNo}/lines/${line._id}`, { headers: hdrs() });
      setPayLines(p => p.filter(x => x._id !== line._id));
    } catch (err) { setError(err.response?.data?.error || 'Delete failed'); }
  };

  if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
      <CircularProgress />
    </Box>
  );

  const nokCount = dependants.filter(d => d.isNextOfKin).length;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 6 }}>

      {/* ── Header card ─────────────────────────────────────────────────────── */}
      <Card elevation={3} sx={{ mb: 2, background: 'linear-gradient(135deg, #546e7a 0%, #37474f 100%)', color: '#fff' }}>
        <CardContent sx={{ py: payrollCtx ? 1.5 : 2.5 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Button startIcon={<ArrowBack />} size="small" variant="outlined"
                onClick={() => navigate(payrollCtx ? fromUrl : '/hr/employees')}
                sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff' } }}>
                Back
              </Button>
              <Box>
                <Typography variant={payrollCtx ? 'subtitle1' : 'h5'} fontWeight={700} lineHeight={1.2}>
                  {emp?.firstName} {emp?.lastName}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} mt={0.5} flexWrap="wrap">
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>{emp?.employeeNo}</Typography>
                  {emp?.jobTitle && <Typography variant="caption" sx={{ opacity: 0.8 }}>· {emp.jobTitle}</Typography>}
                  {payrollCtx ? (
                    <>
                      {emp?.payGroupCode && <Typography variant="caption" sx={{ opacity: 0.8 }}>· Group: {emp.payGroupCode}</Typography>}
                      {emp?.payGradeCode && <Typography variant="caption" sx={{ opacity: 0.8 }}>· Grade: {emp.payGradeCode}{emp?.payStepNo ? `/${emp.payStepNo}` : ''}</Typography>}
                    </>
                  ) : (
                    emp?.department && <Typography variant="caption" sx={{ opacity: 0.8 }}>· {emp.department}</Typography>
                  )}
                  <Chip label={emp?.status} size="small" color={STATUS_COLOR[emp?.status] || 'default'}
                    sx={{ height: 18, fontSize: 11 }} />
                  {payrollCtx && (
                    <Chip label="Pay Lines" size="small" icon={<AttachMoney sx={{ fontSize: 14, color: '#fff !important' }} />}
                      sx={{ height: 18, fontSize: 11, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />
                  )}
                </Box>
              </Box>
            </Box>
            {payrollCtx ? (
              <Box display="flex" gap={1}>
                <Button variant="contained"
                  startIcon={previewLoading ? <CircularProgress size={16} color="inherit" /> : <CalculateOutlined />}
                  onClick={runPreview} disabled={previewLoading}
                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                  {previewLoading ? 'Calculating…' : 'Process Payroll'}
                </Button>
              </Box>
            ) : (
              <Box display="flex" gap={1}>
                <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                  onClick={handleSave} disabled={saving || tab === 3 || tab === 4}
                  sx={{ bgcolor: 'rgba(255,255,255,0.15)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, '&:disabled': { color: 'rgba(255,255,255,0.4)' } }}>
                  {saving ? 'Saving…' : 'Save Changes'}
                </Button>
              </Box>
            )}
          </Box>
        </CardContent>
      </Card>

      {success && <Alert severity="success" sx={{ mb: 1.5 }}>{success}</Alert>}
      {error   && <Alert severity="error"   sx={{ mb: 1.5 }} onClose={() => setError('')}>{error}</Alert>}
      {previewError && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setPreviewError('')}>{previewError}</Alert>}

      {/* ── Tabs ────────────────────────────────────────────────────────────── */}
      <Card elevation={3}>
        <CardContent>
          {!payrollCtx && (
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
              <Tab icon={<Person fontSize="small" />} iconPosition="start" label="Personal" />
              <Tab icon={<Work fontSize="small" />} iconPosition="start" label="Employment & Statutory" />
              <Tab icon={<AccountBalance fontSize="small" />} iconPosition="start" label="Contact & Bank" />
              <Tab icon={<FamilyRestroom fontSize="small" />} iconPosition="start"
                label={<Box display="flex" alignItems="center" gap={0.5}>
                  Dependants
                  {dependants.length > 0 && <Chip label={dependants.length} size="small" sx={{ height: 18, fontSize: 11 }} />}
                </Box>} />
              <Tab icon={<AttachMoney fontSize="small" />} iconPosition="start"
                label={<Box display="flex" alignItems="center" gap={0.5}>
                  Pay Lines
                  {payLines.length > 0 && <Chip label={payLines.length} size="small" color="primary" sx={{ height: 18, fontSize: 11 }} />}
                </Box>} />
            </Tabs>
          )}

          {/* ── Tab 0: Personal ─────────────────────────────────────────────── */}
          <TabPanel value={tab} index={0}>
            <Grid container spacing={2}>
              <Grid item xs={12}><SectionHeader label="Personal Details" /></Grid>
              <F label="First Name *" name="firstName" value={form.firstName} onChange={handle} sm={4} />
              <F label="Last Name *"  name="lastName"  value={form.lastName}  onChange={handle} sm={4} />
              <F label="Gender" name="gender" value={form.gender} onChange={handle} select sm={4}>
                <MenuItem value=""><em>— Not set —</em></MenuItem>
                {GENDERS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </F>
              <F label="Date of Birth" name="dob" value={form.dob} onChange={handle} type="date" sm={4}
                helperText={form.dob ? `Age: ${age(form.dob)} yrs` : ''} />
              <F label="National ID" name="nationalId" value={form.nationalId} onChange={handle} sm={4} />
            </Grid>
          </TabPanel>

          {/* ── Tab 1: Employment & Statutory ────────────────────────────────── */}
          <TabPanel value={tab} index={1}>
            <Grid container spacing={2}>
              <Grid item xs={12}><SectionHeader label="Employment" /></Grid>
              <F label="Job Title" name="jobTitle" value={form.jobTitle} onChange={handle} sm={4} />
              <F label="Department" name="department" value={form.department} onChange={handle} select sm={4}>
                {deptNames.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </F>
              <F label="Status" name="status" value={form.status} onChange={handle} select sm={4}>
                {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </F>
              <F label="Employment Date" name="employmentDate" value={form.employmentDate} onChange={handle} type="date" sm={4} />
              <F label="Termination Date" name="terminationDate" value={form.terminationDate} onChange={handle} type="date" sm={4}
                helperText="Only for proration of final-period allowances" />

              <Grid item xs={12}><SectionHeader label="Pay Structure" /></Grid>
              <F label="Pay Group" name="payGroupCode" value={form.payGroupCode} onChange={handle} select sm={6}>
                <MenuItem value=""><em>— Not assigned —</em></MenuItem>
                {payGroups.map(g => (
                  <MenuItem key={g.groupCode} value={g.groupCode}>
                    {g.name} <span style={{ color: '#999', fontSize: 11, marginLeft: 6 }}>({g.frequency})</span>
                  </MenuItem>
                ))}
              </F>
              <F label="Pay Grade" name="payGradeCode" value={form.payGradeCode} onChange={handle} select sm={6}>
                <MenuItem value=""><em>— Not assigned —</em></MenuItem>
                {payGrades.map(g => (
                  <MenuItem key={g.gradeCode} value={g.gradeCode}>
                    {g.gradeCode} — {g.description}
                  </MenuItem>
                ))}
              </F>
              <F label="Pay Step" name="payStepNo" value={form.payStepNo} onChange={handle} select sm={6}
                helperText={steps.length === 0 && form.payGradeCode ? 'No steps defined for this grade' : ''}>
                <MenuItem value=""><em>— Not assigned —</em></MenuItem>
                {steps.map(s => (
                  <MenuItem key={s.stepNo} value={s.stepNo}>
                    {s.label || `Step ${s.stepNo}`} — {sym()} {fmtN(s.salary)}
                  </MenuItem>
                ))}
              </F>
              <F label="Basic Salary ({sym()})" name="basicSalary" value={form.basicSalary} onChange={handle} type="number" sm={6}
                helperText="Auto-filled from Pay Step; can be overridden" />

              <Grid item xs={12}><SectionHeader label="Statutory (Kenya)" /></Grid>
              <F label="Revenue Authority No." name="kraPin" value={form.kraPin} onChange={handle} sm={4}
                helperText="Kenya Revenue Authority PIN — for PAYE" />
              <F label="NSSF No" name="nssfNo" value={form.nssfNo} onChange={handle} sm={4}
                helperText="National Social Security Fund" />
              <F label="NHIF No" name="nhifNo" value={form.nhifNo} onChange={handle} sm={4}
                helperText="National Health Insurance Fund" />

              <Grid item xs={12}><SectionHeader label="Leave" /></Grid>
              <F label="Leave Group" name="leaveGroupCode" value={form.leaveGroupCode} onChange={handle} select sm={6}>
                <MenuItem value=""><em>— Not assigned —</em></MenuItem>
                {leaveGroups.map(g => (
                  <MenuItem key={g.groupCode} value={g.groupCode}>{g.name}</MenuItem>
                ))}
              </F>
              <F label="Leave Start Date" name="leaveStartDate" value={form.leaveStartDate} onChange={handle}
                type="date" sm={6} helperText="Defaults to employment date if not set" />
              {alBalance !== null && (
                <Grid item xs={12} sm={6}>
                  <Box
                    onClick={() => navigate(`/hr/leave/ledger?employeeNo=${employeeNo}&leaveTypeCode=AL`)}
                    sx={{
                      p: 1.5, borderRadius: 1, border: '1px solid', cursor: 'pointer',
                      borderColor: alBalance <= 0 ? '#c62828aa' : alBalance <= 5 ? '#f57c00aa' : '#2e7d3244',
                      bgcolor:     alBalance <= 0 ? '#ffebee'   : alBalance <= 5 ? '#fff8e1'   : '#f1f8e9',
                      '&:hover': { boxShadow: 2 },
                    }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Annual Leave Balance ({new Date().getFullYear()})
                    </Typography>
                    <Box display="flex" alignItems="baseline" gap={1}>
                      <Typography variant="h5" fontWeight={800} lineHeight={1.2}
                        color={alBalance <= 0 ? 'error.main' : alBalance <= 5 ? 'warning.main' : 'success.main'}>
                        {alBalance}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">days remaining</Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">Click to view ledger →</Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* ── Tab 2: Contact & Bank ─────────────────────────────────────────── */}
          <TabPanel value={tab} index={2}>
            <Grid container spacing={2}>
              <Grid item xs={12}><SectionHeader label="Contact" /></Grid>
              <F label="Email" name="email" value={form.email} onChange={handle} type="email" sm={6} />
              <F label="Phone" name="phone" value={form.phone} onChange={handle} sm={6} />
              <F label="Postal Address" name="postalAddress" value={form.postalAddress} onChange={handle} sm={6}
                helperText="P.O. Box / post office address" />
              <F label="Physical Address" name="physicalAddress" value={form.physicalAddress} onChange={handle} sm={6}
                helperText="Home / residential address" />

              <Grid item xs={12}><SectionHeader label="Bank Details (for salary payment)" /></Grid>
              <F label="Bank Name" name="bankName" value={form.bankName} onChange={handle} sm={4}
                helperText="e.g. Equity Bank, KCB" />
              <F label="Account Number" name="bankAccount" value={form.bankAccount} onChange={handle} sm={4} />
              <F label="Branch" name="bankBranch" value={form.bankBranch} onChange={handle} sm={4} />

              <Grid item xs={12}><SectionHeader label="Payroll Flags" /></Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={!!form.paysPAYE} onChange={e => setForm(f => ({ ...f, paysPAYE: e.target.checked }))} />}
                  label={<Box><Typography variant="body2" fontWeight={600}>Pays PAYE</Typography><Typography variant="caption" color="text.secondary">Uncheck for casual workers or tax-exempt employees</Typography></Box>}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Switch checked={!!form.isPWD} onChange={e => setForm(f => ({ ...f, isPWD: e.target.checked }))} />}
                  label={<Box><Typography variant="body2" fontWeight={600}>Person with Disability (PWD)</Typography><Typography variant="caption" color="text.secondary">Reduces taxable pay by the PWD exemption limit in Payroll Config</Typography></Box>}
                />
              </Grid>
            </Grid>
          </TabPanel>

          {/* ── Tab 3: Dependants & NOK ───────────────────────────────────────── */}
          <TabPanel value={tab} index={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>Dependants &amp; Next of Kin</Typography>
                {nokCount > 0 && (
                  <Typography variant="caption" color="success.main">
                    ✓ {nokCount} next of kin designated
                  </Typography>
                )}
              </Box>
              <Button size="small" variant="contained" startIcon={<Add />} onClick={openAddDep}>
                Add Dependant
              </Button>
            </Box>

            {dependants.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                <FamilyRestroom sx={{ fontSize: 40, opacity: 0.3, display: 'block', mx: 'auto', mb: 1 }} />
                <Typography variant="body2">No dependants added yet.</Typography>
                <Typography variant="caption">Click "Add Dependant" to add family members, next of kin, or emergency contacts.</Typography>
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Relationship</strong></TableCell>
                    <TableCell><strong>Date of Birth</strong></TableCell>
                    <TableCell><strong>Phone</strong></TableCell>
                    <TableCell><strong>Occupation</strong></TableCell>
                    <TableCell align="center"><strong>NOK</strong></TableCell>
                    <TableCell align="right"><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dependants.map(dep => (
                    <TableRow key={dep._id} hover sx={{ '&:nth-of-type(odd)': { bgcolor: 'grey.50' } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{dep.fullName}</Typography>
                        {dep.gender && <Typography variant="caption" color="text.secondary">{dep.gender}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Chip label={dep.relationship} size="small" variant="outlined" color="primary" />
                      </TableCell>
                      <TableCell>
                        {dep.dob ? (
                          <>
                            <Typography variant="body2">{new Date(dep.dob).toLocaleDateString(locale())}</Typography>
                            <Typography variant="caption" color="text.secondary">{age(dep.dob)} yrs</Typography>
                          </>
                        ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{dep.phone || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">{dep.occupation || '—'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {dep.isNextOfKin
                          ? <Tooltip title="Primary Next of Kin"><Star fontSize="small" sx={{ color: '#f9a825' }} /></Tooltip>
                          : <Typography variant="caption" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" color="primary" onClick={() => openEditDep(dep)}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDeleteDep(dep)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TabPanel>

          {/* ── Tab 4: Pay Lines ─────────────────────────────────────────────── */}
          <TabPanel value={tab} index={4}>
            {(() => {
              const txMap = Object.fromEntries(txCodes.map(c => [c.code, c]));
              const getType = (l) => {
                const tx = txMap[l.transactionCode] || txMap[l.code];
                if (tx) return tx.transactionType;
                return l.lineType === 'Allowance' ? 'Income' : 'Deduction';
              };

              // ── Synthetic BASIC from employee/grade/step record ──────────────
              const hasExplicitBasic = payLines.some(l => (l.transactionCode || l.code) === 'BASIC');
              const basicSource = emp?.payGradeCode
                ? `Grade ${emp.payGradeCode}${emp?.payStepNo != null ? ` / Step ${emp.payStepNo}` : ''}`
                : 'Employee record';
              const syntheticBasic = (!hasExplicitBasic && emp?.basicSalary > 0) ? [{
                _id: '__basic__', transactionCode: 'BASIC', description: 'Basic Salary',
                amount: emp.basicSalary, isActive: true, recurringIndex: 999, balance: 0,
                _isSynthetic: true, _source: basicSource,
              }] : [];

              // ── Auto-computed statutory deductions ───────────────────────────
              // These are always calculated by the engine — show them so the user
              // can see what will fire, but they cannot be edited here.
              const STATUTORY_CODES = ['NSSF', 'SHIF', 'PAYE', 'HOUSING_LEVY'];
              const statutoryAuto = STATUTORY_CODES.flatMap(code => {
                const tx = txMap[code];
                if (!tx || tx.isActive === false) return [];
                if (payLines.some(l => (l.transactionCode || l.code) === code)) return [];
                return [{
                  _id: `__auto_${code}__`, transactionCode: code, description: tx.description,
                  amount: 0, isActive: true, recurringIndex: 999, balance: 0,
                  _isSynthetic: true, _isAuto: true,
                }];
              });

              const incomeLines    = [...syntheticBasic, ...payLines.filter(l => getType(l) === 'Income')];
              const deductionLines = [...payLines.filter(l => getType(l) === 'Deduction'), ...statutoryAuto];

              const renderGroup = (groupLabel, lines, bgColor) => (
                <Box mb={3}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight={700}>{groupLabel}</Typography>
                      {lines.filter(l => l.isActive && !l._isAuto).length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          {lines.filter(l => l.isActive && !l._isSynthetic).length > 0
                            ? `${lines.filter(l => l.isActive && !l._isSynthetic).length} configured line(s)`
                            : ''}
                        </Typography>
                      )}
                    </Box>
                    <Button size="small" variant="outlined" startIcon={<Add />} onClick={openAddLine}>
                      Add Line
                    </Button>
                  </Box>
                  {lines.length === 0 ? (
                    <Box sx={{ py: 2.5, textAlign: 'center', color: 'text.disabled', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="body2">No lines yet.</Typography>
                    </Box>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: bgColor }}>
                          <TableCell><strong>Code</strong></TableCell>
                          <TableCell><strong>Description</strong></TableCell>
                          <TableCell align="right"><strong>Amount ({sym()})</strong></TableCell>
                          <TableCell align="right"><strong>Balance</strong></TableCell>
                          <TableCell align="center"><strong>Recurring</strong></TableCell>
                          <TableCell align="center"><strong>Active</strong></TableCell>
                          <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {lines.map(l => {
                          const tx = txMap[l.transactionCode] || txMap[l.code];
                          const isSynthetic = l._isSynthetic;
                          const isAuto      = l._isAuto;
                          return (
                            <TableRow key={l._id} hover
                              sx={{ opacity: l.isActive ? 1 : 0.5, bgcolor: isSynthetic ? 'rgba(0,0,0,0.01)' : undefined }}>
                              <TableCell>
                                <Typography variant="caption" fontFamily="monospace" fontWeight={700}>
                                  {l.transactionCode || l.code}
                                </Typography>
                                {isSynthetic && !isAuto && (
                                  <Tooltip title={`Sourced from: ${l._source}`}>
                                    <Chip label="grade/step" size="small"
                                      sx={{ ml: 0.5, height: 16, fontSize: 10, bgcolor: '#e8f5e9', color: '#2e7d32', cursor: 'help' }} />
                                  </Tooltip>
                                )}
                                {isAuto && (
                                  <Chip label="auto" size="small"
                                    sx={{ ml: 0.5, height: 16, fontSize: 10, bgcolor: '#fff3e0', color: '#e65100' }} />
                                )}
                                {!isSynthetic && tx?.isFormulaBased && (
                                  <Chip label="formula" size="small"
                                    sx={{ ml: 0.5, height: 16, fontSize: 10, bgcolor: '#e3f2fd', color: '#1565c0' }} />
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{l.description}</Typography>
                                {isSynthetic && !isAuto && l._source && (
                                  <Typography variant="caption" color="text.disabled" display="block">
                                    {l._source}
                                  </Typography>
                                )}
                                {isAuto && (
                                  <Typography variant="caption" color="text.disabled" display="block">
                                    Calculated at run time
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" fontWeight={isSynthetic && !isAuto ? 700 : 600}
                                  color={isSynthetic && !isAuto ? '#1565c0' : undefined}>
                                  {isAuto || tx?.isFormulaBased ? '—' : fmtN(l.amount)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {!isSynthetic && (tx?.hasBalance || l.balance > 0) ? (
                                  <Typography variant="body2" color={l.balance > 0 ? 'text.primary' : 'text.disabled'}>
                                    {fmtN(l.balance)}
                                  </Typography>
                                ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                              </TableCell>
                              <TableCell align="center">
                                {!isSynthetic ? (
                                  <Chip
                                    label={l.recurringIndex === 999 ? 'Permanent' : 'One-off'}
                                    size="small" variant="outlined"
                                    color={l.recurringIndex === 999 ? 'default' : 'warning'}
                                    sx={{ fontSize: 10, height: 20 }} />
                                ) : (
                                  <Chip label="Permanent" size="small" variant="outlined"
                                    sx={{ fontSize: 10, height: 20, opacity: 0.5 }} />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {!isSynthetic ? (
                                  <Chip label={l.isActive ? 'Active' : 'Inactive'} size="small"
                                    color={l.isActive ? 'success' : 'default'} />
                                ) : (
                                  <Chip label="Active" size="small" color="success" sx={{ opacity: 0.5 }} />
                                )}
                              </TableCell>
                              <TableCell align="right">
                                {isSynthetic ? (
                                  <Typography variant="caption" color="text.disabled" sx={{ pr: 1 }}>read-only</Typography>
                                ) : (
                                  <>
                                    <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => openEditLine(l)}><Edit fontSize="small" /></IconButton></Tooltip>
                                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteLine(l)}><Delete fontSize="small" /></IconButton></Tooltip>
                                  </>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              );

              return (
                <>
                  {renderGroup('Earnings / Income', incomeLines, '#e8f5e9')}
                  {renderGroup('Deductions', deductionLines, '#fce4ec')}

                  {/* Estimated summary */}
                  {(emp?.basicSalary > 0 || payLines.length > 0) && (
                    <Box sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, mt: 1 }}>
                      <Typography variant="caption" fontWeight={700} color="text.secondary" textTransform="uppercase" letterSpacing={0.8}>
                        Estimated Gross (fixed amounts only)
                      </Typography>
                      <Grid container spacing={2} mt={0.5}>
                        {[
                          { label: 'Basic Salary', val: emp?.basicSalary || 0 },
                          { label: '+ Other Earnings', val: incomeLines.filter(l => !l._isSynthetic && l.isActive && !txMap[l.transactionCode || l.code]?.isFormulaBased).reduce((s, l) => s + (l.amount || 0), 0) },
                          { label: '− Fixed Deductions', val: deductionLines.filter(l => !l._isSynthetic && l.isActive && !txMap[l.transactionCode || l.code]?.isFormulaBased).reduce((s, l) => s + (l.amount || 0), 0) },
                        ].map(({ label, val }) => (
                          <Grid item xs={4} key={label}>
                            <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                            <Typography variant="body2" fontWeight={700}>{sym()} {fmtN(val)}</Typography>
                          </Grid>
                        ))}
                      </Grid>
                      <Typography variant="caption" color="text.disabled">
                        * Statutory deductions (PAYE, SHIF, Housing Levy, NSSF) and formula-based lines are calculated at run time.
                      </Typography>
                    </Box>
                  )}

                  {/* Payslip History — shown in payroll context */}
                  {payrollCtx && (
                    <Box mt={3}>
                      <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                        <Receipt fontSize="small" sx={{ color: '#37474f' }} />
                        <Typography variant="subtitle2" fontWeight={700}>Payslip History</Typography>
                        {payslips.length > 0 && (
                          <Chip label={payslips.length} size="small" color="default" sx={{ height: 18, fontSize: 11 }} />
                        )}
                      </Box>
                      {payslipsLoading ? (
                        <Box display="flex" justifyContent="center" py={2}><CircularProgress size={20} /></Box>
                      ) : payslips.length === 0 ? (
                        <Box sx={{ py: 3, textAlign: 'center', color: 'text.disabled', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                          <Receipt sx={{ fontSize: 32, opacity: 0.3, mb: 0.5 }} />
                          <Typography variant="body2">No payslips yet.</Typography>
                          <Typography variant="caption">Payslips appear here after a payroll run is calculated for this employee.</Typography>
                        </Box>
                      ) : (
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#eceff1' }}>
                              <TableCell><strong>Run</strong></TableCell>
                              <TableCell><strong>Period</strong></TableCell>
                              <TableCell align="right"><strong>Gross Pay</strong></TableCell>
                              <TableCell align="right"><strong>Deductions</strong></TableCell>
                              <TableCell align="right"><strong>Net Pay</strong></TableCell>
                              <TableCell align="center"><strong>Posted</strong></TableCell>
                              <TableCell align="center"></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {payslips.map(ps => (
                              <TableRow key={ps._id} hover>
                                <TableCell>
                                  <Typography variant="caption" fontFamily="monospace" fontWeight={600}>{ps.runNo}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">{ps.periodName || ps.periodCode}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2">{sym()} {fmtN(ps.grossPay)}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" color="error.main">{sym()} {fmtN(ps.totalDeductions)}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={700} color="success.main">{sym()} {fmtN(ps.netPay)}</Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Chip label={ps.isPosted ? 'Posted' : 'Draft'} size="small"
                                    color={ps.isPosted ? 'success' : 'default'} sx={{ height: 18, fontSize: 10 }} />
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="View Payslip">
                                    <IconButton size="small" color="primary"
                                      onClick={() => navigate(`/payroll/runs/${ps.runNo}/payslip/${employeeNo}`)}>
                                      <Receipt fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </Box>
                  )}
                </>
              );
            })()}
          </TabPanel>

        </CardContent>
      </Card>

      {/* ── Documents (contracts, certificates, ID scans…) ─────────────────────── */}
      <Card elevation={3} sx={{ mt: 2 }}>
        <CardContent>
          <AttachmentsPanel refType="Employee" refId={employeeNo} title="Employee Documents" />
        </CardContent>
      </Card>

      {/* ── Dependent dialog ──────────────────────────────────────────────────── */}
      {depGuard.discardDialog}
      <Dialog open={depOpen} onClose={depGuard.attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingDep ? `Edit — ${editingDep.fullName}` : 'Add Dependant / Next of Kin'}
        </DialogTitle>
        <DialogContent dividers>
          {depError && <Alert severity="error" sx={{ mb: 1.5 }}>{depError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}><SectionHeader label="Identity" /></Grid>
            <Grid item xs={12} sm={7}>
              <TextField label="Full Name *" name="fullName" value={depForm.fullName}
                onChange={handleDep} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField select label="Relationship *" name="relationship" value={depForm.relationship}
                onChange={handleDep} fullWidth size="small">
                <MenuItem value=""><em>— Select —</em></MenuItem>
                {RELATIONSHIPS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField select label="Gender" name="gender" value={depForm.gender}
                onChange={handleDep} fullWidth size="small">
                <MenuItem value=""><em>— Not set —</em></MenuItem>
                {GENDERS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Date of Birth" name="dob" type="date" value={depForm.dob}
                onChange={handleDep} fullWidth size="small" InputLabelProps={{ shrink: true }}
                helperText={depForm.dob ? `Age: ${age(depForm.dob)} yrs` : ''} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="National ID" name="nationalId" value={depForm.nationalId}
                onChange={handleDep} fullWidth size="small" />
            </Grid>

            <Grid item xs={12}><SectionHeader label="Contact" /></Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Phone" name="phone" value={depForm.phone}
                onChange={handleDep} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Email" name="email" type="email" value={depForm.email}
                onChange={handleDep} fullWidth size="small" />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Address" name="address" value={depForm.address}
                onChange={handleDep} fullWidth size="small" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Occupation" name="occupation" value={depForm.occupation}
                onChange={handleDep} fullWidth size="small" helperText="e.g. Student, Farmer" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={depForm.isNextOfKin}
                    onChange={e => setDepForm(f => ({ ...f, isNextOfKin: e.target.checked }))}
                    color="warning"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>Primary Next of Kin</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Person to contact in emergencies
                    </Typography>
                  </Box>
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={depGuard.attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveDep} disabled={depSaving}>
            {depSaving ? 'Saving…' : editingDep ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Payroll Line dialog ───────────────────────────────────────────────── */}
      {lineGuard.discardDialog}
      <Dialog open={lineDlg} onClose={lineGuard.attemptClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editLine ? 'Edit Pay Line' : 'Add Pay Line'}</DialogTitle>
        <DialogContent dividers>
          {lineError && <Alert severity="error" sx={{ mb: 1.5 }}>{lineError}</Alert>}
          {(() => {
            const selTx = txCodes.find(c => c.code === lineForm.transactionCode);
            return (
              <Grid container spacing={2}>

                {/* Transaction Code dropdown */}
                <Grid item xs={12}>
                  <TextField select label="Transaction Code *" size="small" fullWidth
                    value={lineForm.transactionCode}
                    onChange={e => {
                      const tx = txCodes.find(c => c.code === e.target.value);
                      setLineForm(f => ({
                        ...f,
                        transactionCode: e.target.value,
                        description: tx?.description || f.description,
                        recurringIndex: tx?.isOneOff ? 1 : f.recurringIndex,
                      }));
                    }}
                    helperText={txCodes.length === 0 ? 'Loading transaction codes…' : 'Select from Transaction Code master'}>
                    <MenuItem value=""><em>— Select a code —</em></MenuItem>
                    {['Income', 'Deduction'].map(type => [
                      <MenuItem key={`hdr-${type}`} disabled
                        sx={{ fontSize: 11, fontWeight: 700, color: type === 'Income' ? '#2e7d32' : '#c62828', opacity: 1 }}>
                        {type === 'Income' ? '── Earnings ──' : '── Deductions ──'}
                      </MenuItem>,
                      ...txCodes.filter(c => c.transactionType === type && c.isActive).map(c => (
                        <MenuItem key={c.code} value={c.code}>
                          <Box display="flex" alignItems="center" gap={1} width="100%">
                            <Typography variant="caption" fontFamily="monospace" fontWeight={700} sx={{ minWidth: 90 }}>{c.code}</Typography>
                            <Typography variant="body2" color="text.secondary">{c.description}</Typography>
                            {c.isFormulaBased && (
                              <Chip label="formula" size="small"
                                sx={{ ml: 'auto', height: 16, fontSize: 10, bgcolor: '#e3f2fd', color: '#1565c0' }} />
                            )}
                          </Box>
                        </MenuItem>
                      )),
                    ])}
                  </TextField>
                </Grid>

                {/* Description */}
                <Grid item xs={12}>
                  <TextField label="Description" size="small" fullWidth value={lineForm.description}
                    onChange={e => setLineForm(f => ({ ...f, description: e.target.value }))}
                    helperText="Auto-filled from code — edit if needed" />
                </Grid>

                {/* Formula badge */}
                {selTx?.isFormulaBased && (
                  <Grid item xs={12}>
                    <Box sx={{ p: 1, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                      <Typography variant="caption" color="primary.main">
                        <strong>Formula-based</strong> — amount computed by the payroll engine using the formula on the transaction code. The amount field below is ignored unless you want to override.
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {/* Amount */}
                <Grid item xs={selTx?.hasBalance ? 6 : 8}>
                  <TextField label={selTx?.isFormulaBased ? 'Override Amount ({sym()}, 0 = use formula)' : 'Amount ({sym()})'}
                    type="number" size="small" fullWidth value={lineForm.amount}
                    onChange={e => setLineForm(f => ({ ...f, amount: Number(e.target.value) }))} />
                </Grid>

                {/* Balance — only for codes with hasBalance */}
                {selTx?.hasBalance && (
                  <Grid item xs={6}>
                    <TextField label={`Opening Balance (${sym()})`} type="number" size="small" fullWidth
                      value={lineForm.balance}
                      onChange={e => setLineForm(f => ({ ...f, balance: Number(e.target.value) }))}
                      helperText="Remaining amount for loan / advance" />
                  </Grid>
                )}

                {/* Recurring Index */}
                <Grid item xs={4}>
                  <TextField select label="Recurring" size="small" fullWidth
                    value={lineForm.recurringIndex === 999 ? 999 : 1}
                    onChange={e => setLineForm(f => ({ ...f, recurringIndex: Number(e.target.value) }))}>
                    <MenuItem value={999}>Permanent</MenuItem>
                    <MenuItem value={1}>One-off (clears after posting)</MenuItem>
                  </TextField>
                </Grid>

                {/* Active */}
                <Grid item xs={12}>
                  <TextField select label="Status" size="small" fullWidth
                    value={lineForm.isActive ? 'active' : 'inactive'}
                    onChange={e => setLineForm(f => ({ ...f, isActive: e.target.value === 'active' }))}>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive (excluded from calculations)</MenuItem>
                  </TextField>
                </Grid>

              </Grid>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={lineGuard.attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveLine} disabled={lineSaving}>
            {lineSaving ? 'Saving…' : editLine ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirm Remove Dependant ─────────────────────────────────────────── */}
      <Dialog open={Boolean(removeDep)} onClose={() => setRemoveDep(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Dependant</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{removeDep?.fullName}</strong> from dependants?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDep(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmRemoveDep}>Remove</Button>
        </DialogActions>
      </Dialog>

      {/* ── Confirm Remove Pay Line ──────────────────────────────────────────── */}
      <Dialog open={Boolean(removeLine)} onClose={() => setRemoveLine(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove Pay Line</DialogTitle>
        <DialogContent>
          <Typography>
            Remove <strong>{removeLine?.description || removeLine?.transactionCode}</strong> from pay lines?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveLine(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmRemoveLine}>Remove</Button>
        </DialogActions>
      </Dialog>

      {/* ── Payroll Preview Dialog ───────────────────────────────────────────── */}
      <Dialog open={previewDlg} onClose={() => setPreviewDlg(false)} maxWidth="sm" fullWidth>
        {previewResult && (() => {
          const pr = previewResult;
          const incomeLines = pr.earnings || [];
          const dedLines    = pr.deductions || [];
          return (
            <>
              <DialogTitle sx={{ pb: 0 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CalculateOutlined color="primary" />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                      Payroll Preview — {pr.employeeName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {pr.salarySource} · Basic: {sym()} {fmtN(pr.basicSalary)}
                    </Typography>
                  </Box>
                </Box>
              </DialogTitle>
              <DialogContent dividers sx={{ p: 0 }}>

                {/* Earnings */}
                <Box px={2} pt={1.5} pb={0.5}>
                  <Typography variant="caption" fontWeight={700} color="success.main" textTransform="uppercase" letterSpacing={0.8}>
                    Earnings
                  </Typography>
                </Box>
                <Table size="small">
                  <TableBody>
                    {incomeLines.map(l => (
                      <TableRow key={l.code}>
                        <TableCell sx={{ pl: 2 }}>
                          <Typography variant="body2" fontFamily="monospace" fontWeight={700} fontSize={12}>{l.code}</Typography>
                        </TableCell>
                        <TableCell><Typography variant="body2">{l.description}</Typography></TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>{sym()} {fmtN(l.amount)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                      <TableCell colSpan={2} sx={{ pl: 2 }}>
                        <Typography variant="body2" fontWeight={700}>Gross Pay</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700}>{sym()} {fmtN(pr.grossPay)}</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                {/* Deductions */}
                <Box px={2} pt={1.5} pb={0.5}>
                  <Typography variant="caption" fontWeight={700} color="error.main" textTransform="uppercase" letterSpacing={0.8}>
                    Deductions
                  </Typography>
                </Box>
                <Table size="small">
                  <TableBody>
                    {dedLines.map(l => (
                      <TableRow key={l.code}>
                        <TableCell sx={{ pl: 2 }}>
                          <Typography variant="body2" fontFamily="monospace" fontWeight={700} fontSize={12}>{l.code}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <Typography variant="body2">{l.description}</Typography>
                            {l.isStatutory && <Chip label="statutory" size="small" sx={{ height: 14, fontSize: 10 }} />}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" color="error.main" fontWeight={500}>{sym()} {fmtN(l.amount)}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#fce4ec' }}>
                      <TableCell colSpan={2} sx={{ pl: 2 }}>
                        <Typography variant="body2" fontWeight={700}>Total Deductions</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={700} color="error.main">{sym()} {fmtN(pr.totalDeductions)}</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>

                {/* Net Pay summary */}
                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderTop: '2px solid', borderColor: 'divider' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">Taxable Pay</Typography>
                      <Typography variant="body2">{sym()} {fmtN(pr.taxablePay)}</Typography>
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="caption" color="text.secondary" display="block">Net Pay</Typography>
                      <Typography variant="h6" fontWeight={800} color="success.main">{sym()} {fmtN(pr.netPay)}</Typography>
                    </Box>
                  </Box>
                  {pr.aThirdRuleViolated && (
                    <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                      A-Third Rule violated — net pay is less than 1/3 of basic salary.
                    </Alert>
                  )}
                </Box>
              </DialogContent>
              <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, px: 2, pb: 2 }}>
                {applyDone && (
                  <Alert severity="success" sx={{ py: 0.5 }}>
                    Pay lines updated — basic salary set to {sym()} {fmtN(pr.basicSalary)} and non-statutory earnings saved.
                  </Alert>
                )}
                <Box display="flex" justifyContent="flex-end" gap={1}>
                  <Button onClick={() => { setPreviewDlg(false); setApplyDone(false); }}>Close</Button>
                  {!applyDone && (
                    <Tooltip title={`Save basic salary (${sym()} ${fmtN(pr.basicSalary)}) and upsert pay lines for all non-statutory, non-formula earnings`}>
                      <span>
                        <Button variant="contained" color="primary"
                          startIcon={applyLoading ? <CircularProgress size={14} color="inherit" /> : <Save />}
                          onClick={applyPreviewToPayLines} disabled={applyLoading}>
                          {applyLoading ? 'Applying…' : 'Apply to Pay Lines'}
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </DialogActions>
            </>
          );
        })()}
      </Dialog>

    </Box>
  );
};

export default EmployeeCard;
